/**
 * ОДИНАКОВЫЕ ЗАМЕЧАНИЯ — ОДНА СТРОКА С ЧИСЛОМ, А НЕ СПИСОК ИЗ СОТЕН.
 *
 * Перенесено из донора (PRO100-онлайн, `src/ui/readiness-issues-presenter.ts`,
 * живой замер 2026-08-31: 460 строк → 4 уникальных заголовка).
 *
 * Измерено НА НАШИХ ДАННЫХ 2026-09-07 (снимок прод-БД `_prod_db_snapshot/holzcom.db`,
 * локальная тестовая сцена `test_scene`, backend `/validate-graph` и `/ergonomics`):
 *
 *   validate-graph: 60 замечаний, ВСЕ с rule="material_id_missing"
 *     (разные entity — стены/потолок/пол/плинтус каждой из 6 комнат) → 1 заголовок.
 *   ergonomics:      9 замечаний → 2 заголовка:
 *     "Столешница на …мм (норма 820–920)"  ×8 (разные модули, число в тексте разное)
 *     "Проход …мм (комфорт ≥900)"          ×1
 *
 * У донора заголовок и человеческая подпись строки — РАЗНЫЕ поля (title / subject):
 * заголовок фиксирован для вида замечания, подпись меняется по объекту. У нас это
 * различие не хранится отдельно — `message` уже содержит и общий смысл, И
 * идентификатор/число объекта в одной строке ("Wall 'wall_R01_N' has no materialId",
 * "Столешница на 720мм …"). Поэтому ключ группировки — НЕ дословный `message`
 * (тогда группы не появятся вовсе, у каждого свой текст), а `code` (`concern`/`rule`
 * для validate-graph, `rule` для ergonomics) — тот же признак, что уже показывается
 * как бейдж в UI. Внутри группы читателю показывается каждый `message` отдельной
 * строкой — там и остаётся отличающее число/идентификатор.
 */
export type GroupableIssue = Readonly<{ code: string }>;

export type IssueGroup<T extends GroupableIssue> = Readonly<{ code: string; items: readonly T[] }>;

/**
 * Группирует по `code`, крупные группы — вперёд (самая частая проблема — главная
 * новость списка, а не последняя строка после прокрутки).
 */
export function groupIssuesByCode<T extends GroupableIssue>(issues: readonly T[]): readonly IssueGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of issues) {
    const bucket = groups.get(item.code);
    if (bucket) bucket.push(item);
    else groups.set(item.code, [item]);
  }
  return [...groups.entries()]
    .map(([code, items]): IssueGroup<T> => ({ code, items }))
    .sort((a, b) => b.items.length - a.items.length || a.code.localeCompare(b.code));
}

/** Русский счёт без библиотеки: «1 объект» / «2 объекта» / «5 объектов». */
export function pluralRu(count: number, one: string, few: string, many: string): string {
  const tens = count % 100;
  if (tens >= 11 && tens <= 14) return many;
  const units = count % 10;
  return units === 1 ? one : units >= 2 && units <= 4 ? few : many;
}
